import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useClickOutside } from '../hooks/useClickOutside';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useScrollDirection } from '../hooks/useScrollDirection';
import { PhotoIcon, SettingsIcon, HomeIcon, AlbumsIcon, FriendsIcon, MapIcon } from './NavIcons';

const Navigation: React.FC = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const scrollDirection = useScrollDirection();
  // Hide on mobile when scrolling down past the header; reveal on scroll-up,
  // when at the top of the page, or when the mobile menu is open.
  const hiddenOnMobile = scrollDirection === 'down' && !mobileMenuOpen;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close on Escape key
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  // Close on click outside
  useClickOutside(mobileMenuRef, () => setMobileMenuOpen(false), mobileMenuOpen);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const navLinkClass = (path: string) => `
    relative px-4 py-2 rounded-xl font-medium transition-all duration-300
    ${isActive(path)
      ? 'text-primary-blue dark:text-primary-blue-light bg-primary-blue/10 dark:bg-primary-blue/20'
      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
    }
  `;

  const mobileNavLinkClass = (path: string) => `
    flex items-center gap-3 w-full px-4 py-3 rounded-xl font-medium transition-all duration-300 min-h-[44px]
    ${isActive(path)
      ? 'text-primary-blue dark:text-primary-blue-light bg-primary-blue/10 dark:bg-primary-blue/20'
      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
    }
  `;

  return (
    <nav 
      ref={mobileMenuRef}
      className={`
        sticky top-0 z-40 transition-all duration-300
        ${hiddenOnMobile ? '-translate-y-full sm:translate-y-0' : 'translate-y-0'}
        ${scrolled 
          ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl shadow-lg shadow-gray-200/50 dark:shadow-gray-900/50' 
          : 'bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm'
        }
      `}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Brand */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-blue to-accent-purple flex items-center justify-center shadow-lg group-hover:shadow-glow transition-all duration-300 group-hover:scale-110">
              <PhotoIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent hidden sm:block">
              Private Photos
            </span>
          </Link>

          {/* Desktop Navigation Links — hidden on mobile */}
          <div className="hidden sm:flex items-center gap-2">
            <Link to="/" className={navLinkClass('/')}>
              Home
            </Link>
            <Link to="/photos" className={navLinkClass('/photos')}>
              Photos
            </Link>

            <Link to="/library" className={navLinkClass('/library')}>
              My Library
            </Link>
            <Link to="/social" className={navLinkClass('/social')}>
              Social
            </Link>
            <Link to="/map" className={navLinkClass('/map')}>
              Map
            </Link>

            {/* Divider */}
            {isAuthenticated && (
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-2" />
            )}

            {/* Settings — only when authenticated */}
            {isAuthenticated && (
              <Link 
                to="/settings" 
                className="p-2 rounded-xl text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300"
                aria-label="Settings"
              >
                <SettingsIcon />
              </Link>
            )}
          </div>

          {/* Mobile: Hamburger button */}
          <div className="flex sm:hidden items-center gap-2">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-xl text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen ? "true" : "false"}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      <div
        className={`
          sm:hidden overflow-hidden transition-all duration-300 ease-in-out
          ${mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}
        `}
        aria-hidden={!mobileMenuOpen ? "true" : "false"}
      >
        <div className="container mx-auto px-4 pb-4 space-y-1 border-t border-gray-200/50 dark:border-gray-700/50 pt-3">
          <Link to="/" className={mobileNavLinkClass('/')} tabIndex={mobileMenuOpen ? 0 : -1}>
            <HomeIcon className="w-5 h-5 flex-shrink-0" />
            Home
          </Link>

          <Link to="/photos" className={mobileNavLinkClass('/photos')} tabIndex={mobileMenuOpen ? 0 : -1}>
            <PhotoIcon className="w-5 h-5 flex-shrink-0" />
            Photos
          </Link>

          <Link to="/library" className={mobileNavLinkClass('/library')} tabIndex={mobileMenuOpen ? 0 : -1}>
            <AlbumsIcon className="w-5 h-5 flex-shrink-0" />
            My Library
          </Link>

          <Link to="/social" className={mobileNavLinkClass('/social')} tabIndex={mobileMenuOpen ? 0 : -1}>
            <FriendsIcon className="w-5 h-5 flex-shrink-0" />
            Social
          </Link>

          <Link to="/map" className={mobileNavLinkClass('/map')} tabIndex={mobileMenuOpen ? 0 : -1}>
            <MapIcon className="w-5 h-5 flex-shrink-0" />
            Map
          </Link>

          {isAuthenticated && (
            <>
              <div className="border-t border-gray-200/50 dark:border-gray-700/50 my-2" />
              <Link to="/settings" className={mobileNavLinkClass('/settings')} tabIndex={mobileMenuOpen ? 0 : -1}>
                <SettingsIcon className="w-5 h-5 flex-shrink-0" />
                Settings
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
